import { useState, type ReactElement } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { apiClient } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { AdminNav } from "./AdminPage";

/**
 * Cycle 17 STORY-1704 — Admin Calls UI (Novofon click-to-call).
 *
 * Backend (Cycle 14) live:
 * - POST /api/admin/calls — initiate Novofon callback (sales agent rings → connects to lead)
 * - GET  /api/admin/calls — paginated list
 * - PATCH /api/admin/calls/{id} — update status + notes + recording_url
 *
 * UI:
 * - List recent calls
 * - Form: phone + lead_id (optional) + notes → triggers Novofon
 * - Status update + recording playback (if Novofon attached recording_url)
 */

const STATUSES = ["pending", "in_progress", "done", "cancelled"] as const;
type CallStatus = (typeof STATUSES)[number];

const STATUS_LABELS: Record<CallStatus, { ru: string; color: string; bg: string }> = {
  pending: { ru: "Ожидает", color: "#a07000", bg: "#fff7d6" },
  in_progress: { ru: "В процессе", color: "#0b5fff", bg: "#e6f0ff" },
  done: { ru: "Завершён", color: "#0a7a4a", bg: "#e6f7ee" },
  cancelled: { ru: "Отменён", color: "#a01c2a", bg: "#fde8eb" },
};

type Call = {
  id: string;
  lead_id: string | null;
  phone: string;
  contact_name: string | null;
  company: string | null;
  requested_by_admin: string | null;
  notes: string | null;
  status: string;
  recording_url: string | null;
  requested_at: string;
  completed_at: string | null;
};

type CallList = {
  items: Call[];
  total: number;
  page: number;
  page_size: number;
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function AdminCallsPage(): ReactElement {
  const { hasAdminJwt } = useAuth();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<CallStatus | "">("");
  const [showForm, setShowForm] = useState(false);
  const [formPhone, setFormPhone] = useState("");
  const [formContactName, setFormContactName] = useState("");
  const [formCompany, setFormCompany] = useState("");
  const [formLeadId, setFormLeadId] = useState("");
  const [formNotes, setFormNotes] = useState("");

  const { data, isLoading } = useQuery<CallList>({
    queryKey: ["admin-calls", statusFilter],
    queryFn: async () => {
      const params: Record<string, string | number> = { page_size: 50 };
      if (statusFilter) params.status = statusFilter;
      const res = await apiClient.get<CallList>("/api/admin/calls", { params });
      return res.data;
    },
    enabled: hasAdminJwt,
    refetchInterval: 30_000,
  });

  const createCall = useMutation({
    mutationFn: async () => {
      const payload = {
        phone: formPhone,
        contact_name: formContactName || null,
        company: formCompany || null,
        lead_id: formLeadId || null,
        notes: formNotes || null,
      };
      const res = await apiClient.post("/api/admin/calls", payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-calls"] });
      setShowForm(false);
      setFormPhone("");
      setFormContactName("");
      setFormCompany("");
      setFormLeadId("");
      setFormNotes("");
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: CallStatus }) => {
      const res = await apiClient.patch(`/api/admin/calls/${id}`, { status });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-calls"] });
    },
  });

  if (!hasAdminJwt) {
    return (
      <section style={{ padding: 32, maxWidth: 600, margin: "0 auto" }}>
        <h1>Admin Calls</h1>
        <p style={{ color: "#5a6378" }}>
          Требуется admin authentication. <Link to="/admin">Войти как admin</Link>
        </p>
      </section>
    );
  }

  return (
    <section style={{ maxWidth: 1300, margin: "0 auto" }}>
      <AdminNav />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap", gap: 12, padding: "0 24px" }}>
        <div>
          <h1 style={{ margin: "0 0 4px" }}>Calls ({data?.total ?? "..."})</h1>
          <p style={{ margin: 0, fontSize: 13, color: "#5a6378" }}>
            Click-to-call через Novofon · Cycle 14+17
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: "8px 14px",
            background: showForm ? "#a01c2a" : "#0b5fff",
            color: "white",
            border: 0,
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {showForm ? "× Закрыть" : "📞 Перезвонить лиду"}
        </button>
      </header>

      {showForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createCall.mutate();
          }}
          style={{ padding: 16, background: "#f5f7fc", borderRadius: 8, marginBottom: 16 }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label>
              <span style={{ display: "block", fontSize: 12, color: "#5a6378", marginBottom: 4 }}>Телефон *</span>
              <input
                type="tel"
                required
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
                placeholder="+7 495 1234567"
                style={{ width: "100%", padding: 8, border: "1px solid #d1d5e0", borderRadius: 4 }}
              />
            </label>
            <label>
              <span style={{ display: "block", fontSize: 12, color: "#5a6378", marginBottom: 4 }}>Имя</span>
              <input
                type="text"
                value={formContactName}
                onChange={(e) => setFormContactName(e.target.value)}
                style={{ width: "100%", padding: 8, border: "1px solid #d1d5e0", borderRadius: 4 }}
              />
            </label>
            <label>
              <span style={{ display: "block", fontSize: 12, color: "#5a6378", marginBottom: 4 }}>Компания</span>
              <input
                type="text"
                value={formCompany}
                onChange={(e) => setFormCompany(e.target.value)}
                style={{ width: "100%", padding: 8, border: "1px solid #d1d5e0", borderRadius: 4 }}
              />
            </label>
            <label>
              <span style={{ display: "block", fontSize: 12, color: "#5a6378", marginBottom: 4 }}>Lead UUID (опционально)</span>
              <input
                type="text"
                value={formLeadId}
                onChange={(e) => setFormLeadId(e.target.value)}
                placeholder="abc12345-..."
                style={{ width: "100%", padding: 8, border: "1px solid #d1d5e0", borderRadius: 4, fontFamily: "monospace", fontSize: 12 }}
              />
            </label>
          </div>
          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#5a6378", marginBottom: 4 }}>Заметки</span>
            <textarea
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              rows={2}
              style={{ width: "100%", padding: 8, border: "1px solid #d1d5e0", borderRadius: 4 }}
            />
          </label>
          <button
            type="submit"
            disabled={createCall.isPending}
            style={{
              marginTop: 12,
              padding: "10px 20px",
              background: "#0a7a4a",
              color: "white",
              border: 0,
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: createCall.isPending ? "not-allowed" : "pointer",
            }}
          >
            {createCall.isPending ? "Запрашиваем..." : "📞 Позвонить через Novofon"}
          </button>
          {createCall.isError && (
            <p style={{ color: "#a01c2a", marginTop: 8, fontSize: 12 }}>
              Ошибка: {(createCall.error as Error).message}
            </p>
          )}
          {createCall.isSuccess && (
            <p style={{ color: "#0a7a4a", marginTop: 8, fontSize: 12 }}>✓ Запрос на callback создан</p>
          )}
        </form>
      )}

      <div style={{ marginBottom: 16 }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CallStatus | "")}
          style={{ padding: "8px 12px", border: "1px solid #d1d5e0", borderRadius: 6 }}
        >
          <option value="">Все статусы</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s].ru}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <p>Загрузка...</p>}

      {data && data.items.length === 0 && (
        <div style={{ padding: 48, textAlign: "center", color: "#8693b0", border: "1px dashed #d1d5e0", borderRadius: 12 }}>
          Звонков ещё нет. Создайте первый через форму выше.
        </div>
      )}

      {data && data.items.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "white", border: "1px solid #e1e6f0", borderRadius: 8, overflow: "hidden" }}>
          <thead>
            <tr style={{ background: "#f5f7fc", fontSize: 12, color: "#5a6378", textAlign: "left" }}>
              <th style={{ padding: 10 }}>Дата</th>
              <th style={{ padding: 10 }}>Контакт</th>
              <th style={{ padding: 10 }}>Телефон</th>
              <th style={{ padding: 10 }}>Sales</th>
              <th style={{ padding: 10 }}>Статус</th>
              <th style={{ padding: 10 }}>Запись</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((c) => (
              <tr key={c.id} style={{ borderTop: "1px solid #e1e6f0", fontSize: 13 }}>
                <td style={{ padding: 10, color: "#5a6378", whiteSpace: "nowrap" }}>{formatDate(c.requested_at)}</td>
                <td style={{ padding: 10 }}>
                  <div style={{ fontWeight: 600 }}>{c.contact_name || "—"}</div>
                  {c.company && <div style={{ fontSize: 11, color: "#8693b0" }}>{c.company}</div>}
                  {c.lead_id && (
                    <div style={{ fontSize: 11, color: "#8693b0" }}>
                      Lead: <code>{c.lead_id.slice(0, 8)}</code>
                    </div>
                  )}
                </td>
                <td style={{ padding: 10, fontFamily: "monospace" }}>{c.phone}</td>
                <td style={{ padding: 10, fontSize: 12, color: "#5a6378" }}>{c.requested_by_admin ?? "—"}</td>
                <td style={{ padding: 10 }}>
                  <select
                    value={c.status}
                    onChange={(e) => updateStatus.mutate({ id: c.id, status: e.target.value as CallStatus })}
                    style={{
                      padding: "4px 8px",
                      border: "1px solid",
                      borderColor: STATUS_LABELS[c.status as CallStatus]?.color ?? "#d1d5e0",
                      borderRadius: 4,
                      fontSize: 12,
                      background: STATUS_LABELS[c.status as CallStatus]?.bg ?? "white",
                      color: STATUS_LABELS[c.status as CallStatus]?.color ?? "#0a1340",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s].ru}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: 10 }}>
                  {c.recording_url ? (
                    <audio controls src={c.recording_url} style={{ height: 28, maxWidth: 180 }} />
                  ) : (
                    <span style={{ fontSize: 11, color: "#8693b0" }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
